import { createSelector } from 'reselect'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { sortBy } from 'lib/array/arrayUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import getTourV2DepartureDisplayPrice from 'lib/tours/getTourV2DepartureDisplayPrice'
import { isTourV2Item } from 'lib/checkout/checkoutUtils'
import { TOUR_V2_PRIVATE_REQUEST_EXCLUDED_REGIONS } from 'constants/config/region'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { startOfMonth } from 'lib/datetime/dateUtils'
import { hasAvailableTourBundleRooms } from 'lib/tours/tourBundleRoomAvailability'

export const getTourV2CartItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (checkoutCartItems): Array<App.Checkout.TourV2Item> => checkoutCartItems?.filter((cartItem): cartItem is App.Checkout.TourV2Item => isTourV2Item(cartItem)) ?? [],
)

export const getTourV2OfferPageBaseUrl = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer?.id,
  (tourV2OfferId) => `/tour/${tourV2OfferId}`,
)

export function isTourPurchasable(tourOffer: App.Tours.TourV2Offer) {
  return !!tourOffer.purchasableOptions.length && Object.values(tourOffer.departures).some((d) => d.status === 'available' && hasAvailableTourBundleRooms(d, 1))
}

export const getIsTourOfferPurchasable = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer,
  isTourPurchasable,
)

export const getCheapestPurchasableOption = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer,
  (offerPageState: OfferPageState) => offerPageState.tourV2Variation,
  (offerPageState: OfferPageState, appState: App.State) => getIsLuxPlusToursEnabled(appState),
  (
    offer,
    variation,
    isLuxPlusToursAvailable,
  ): App.Tours.TourV2OfferPurchasableOption | undefined => {
    if (offer && variation) {
      const hasBundledPrice = offer.purchasableOptions.some(option =>
        option.fkVariationId === variation.id &&
        !!option.ltleBundleId &&
        offer.departures[option.fkDepartureId]?.status === 'available',
      )
      return findCheapestTourV2PurchasableOption(
        offer,
        variation.id,
        isLuxPlusToursAvailable,
        {
          purchasableOptionPredicate: option => !hasBundledPrice || !!option.ltleBundleId,
          getEffectivePrice: option => getTourV2DepartureDisplayPrice(option, isLuxPlusToursAvailable),
        },
      )
    }
  },
)

export const getVariationsDepartureBoundaries = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer?.variations,
  (offerPageState: OfferPageState) => offerPageState.tourV2Offer?.departures,
  (
    variations,
    departures,
  ): App.Tours.V2OfferVariationsDepartureBoundaries => {
    const variationsAvailableDepartureBoundaries: App.Tours.V2OfferVariationsDepartureBoundaries = {}
    const variationsUnavailableDepartureBoundaries: App.Tours.V2OfferVariationsDepartureBoundaries = {}

    for (const departureId in departures) {
      const departure = departures[departureId]
      const variationsDepartureBoundaries = departure.status === 'available' ? variationsAvailableDepartureBoundaries : variationsUnavailableDepartureBoundaries
      if (!variationsDepartureBoundaries[departure.fkVariationId]) {
        variationsDepartureBoundaries[departure.fkVariationId] = {
          earliestDeparture: undefined,
          latestDeparture: undefined,
        }
      }
      const variationDepartureBoundaries = variationsDepartureBoundaries[departure.fkVariationId]

      if (
        variationDepartureBoundaries.earliestDeparture === undefined ||
        new Date(departure.startDate) < new Date(variationDepartureBoundaries.earliestDeparture.startDate)
      ) {
        variationDepartureBoundaries.earliestDeparture = departure
      }
      if (
        variationDepartureBoundaries.latestDeparture === undefined ||
        new Date(departure.startDate) > new Date(variationDepartureBoundaries.latestDeparture.startDate)
      ) {
        variationDepartureBoundaries.latestDeparture = departure
      }
    }

    for (const variationId in variations) {
      variationsAvailableDepartureBoundaries[variationId] ??= variationsUnavailableDepartureBoundaries[variationId]
    }

    return variationsAvailableDepartureBoundaries
  },
)

export const getSelectedVariationDepartureBoundaries = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Variation,
  (offerPageState: OfferPageState) => getVariationsDepartureBoundaries(offerPageState),
  (selectedVariation, variationsDepartureBoundaries) => selectedVariation ? variationsDepartureBoundaries[selectedVariation.id] : undefined,
)

export const getSortedItinerary = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Variation!,
  (variation) => sortBy(
    variation.itinerary,
    event => event.startDay,
    'asc',
  ),
)

export function isPrivateRequestEnabled(variation: App.Tours.TourV2OfferVariation, regionCode: string): boolean {
  return variation.canRequestPrivateDepartures && !TOUR_V2_PRIVATE_REQUEST_EXCLUDED_REGIONS.includes(regionCode)
}

export const getIsPrivateRequestEnabled = createSelector(
  (offerPageState: OfferPageState) => offerPageState.tourV2Variation!,
  (offerPageState: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  isPrivateRequestEnabled,
)

const MIN_VISIBLE_DEPARTURES = 6

export const getCalendarDeparturesWithAnyBundleRooms = createSelector(
  (state: OfferPageState) => state.tourV2Variation?.departures,
  (state: OfferPageState) => state.departureYear,
  (departures = [], departureYear) => {
    const availableDepartures = departures.filter(departure => hasAvailableTourBundleRooms(departure, 1))
    if (departureYear) {
      return availableDepartures.filter(departure => new Date(departure.startDate).getFullYear() === departureYear)
    }
    return availableDepartures
  },
)

export const getCalendarDepartures = createSelector(
  getCalendarDeparturesWithAnyBundleRooms,
  (state: OfferPageState) => state.rooms.length,
  (departures, roomCount) => departures.filter(departure => hasAvailableTourBundleRooms(departure, roomCount)),
)

export const getFilteredDepartures = createSelector(
  getCalendarDepartures,
  (state: OfferPageState) => state.departureMonth,
  (departures, departureMonth) => {
    if (departureMonth) {
      const filterDate = startOfMonth(new Date(departureMonth.year, departureMonth.month))
      const nextMonthDate = startOfMonth(new Date(departureMonth.year, departureMonth.month + 1))

      const selectedMonthDepartures = departures.filter(departure => {
        const depDate = new Date(departure.startDate)
        return depDate >= filterDate && depDate < nextMonthDate
      })

      if (selectedMonthDepartures.length >= MIN_VISIBLE_DEPARTURES) {
        return selectedMonthDepartures
      }

      // Fill from following months (always including full months) until we reach the minimum
      const result = [...selectedMonthDepartures]

      const futureDepartures = sortBy(
        departures.filter(dep => new Date(dep.startDate) >= nextMonthDate),
        dep => new Date(dep.startDate),
        'asc',
      )

      let currentMonthKey = ''
      let currentMonthBatch: Array<App.Tours.TourV2OfferDeparture> = []

      for (const dep of futureDepartures) {
        const depDate = new Date(dep.startDate)
        const monthKey = `${depDate.getFullYear()}-${depDate.getMonth()}`

        if (monthKey !== currentMonthKey) {
          if (currentMonthBatch.length) {
            result.push(...currentMonthBatch)
          }
          if (result.length >= MIN_VISIBLE_DEPARTURES) break
          currentMonthKey = monthKey
          currentMonthBatch = [dep]
        } else {
          currentMonthBatch.push(dep)
        }
      }

      if (result.length < MIN_VISIBLE_DEPARTURES && currentMonthBatch.length) {
        result.push(...currentMonthBatch)
      }

      return result
    }
    return departures
  },
)
